import React, { useEffect, useMemo, useState } from 'react';
import { Eye, FolderCog, Lock, Pencil, Settings2, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { sheetService } from '../../services/sheetService';
import { sheetWorkspaceService } from '../../services/sheetWorkspaceService';
import { userService } from '../../services/userService';
import organizationService from '../../services/organizationService';
import { getSheetPath, getWorkspaceEditPath, getWorkspacePath } from '../../utils/sheetDomains';
import SheetsHeader from './SheetsHeader';

const formatDate = (timestamp) => {
  if (!timestamp) return 'Unknown';
  const value = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return value.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const SheetsAdminAccess = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [sheets, setSheets] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [pendingOrganizations, setPendingOrganizations] = useState([]);
  const [reviewingOrganizationId, setReviewingOrganizationId] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let isActive = true;

    const load = async () => {
      if (!user?.uid) {
        if (!isActive) return;
        setLoading(false);
        setIsAdmin(false);
        setSheets([]);
        setWorkspaces([]);
        return;
      }

      setLoading(true);
      setError('');

      try {
        const openUser = await userService.getUserProfile(user.uid).catch(() => null);
        const nextIsAdmin = `${openUser?.role || ''}`.trim() === 'admin';

        if (!isActive) return;
        setIsAdmin(nextIsAdmin);

        if (!nextIsAdmin) {
          setSheets([]);
          setWorkspaces([]);
          return;
        }

        const [sheetResult, workspaceResult, organizationResult] = await Promise.allSettled([
          sheetService.getAdminAccessibleSheets(),
          sheetWorkspaceService.getAdminAccessibleWorkspaces(),
          organizationService.getPendingOrganizations()
        ]);

        if (!isActive) return;
        setSheets(sheetResult.status === 'fulfilled' && Array.isArray(sheetResult.value) ? sheetResult.value : []);
        setWorkspaces(workspaceResult.status === 'fulfilled' && Array.isArray(workspaceResult.value) ? workspaceResult.value : []);
        setPendingOrganizations(
          organizationResult.status === 'fulfilled' && Array.isArray(organizationResult.value)
            ? organizationResult.value
            : []
        );

        const failedSections = [
          sheetResult.status === 'rejected' ? `sheets: ${sheetResult.reason?.message || 'permission denied'}` : '',
          workspaceResult.status === 'rejected' ? `workspaces: ${workspaceResult.reason?.message || 'permission denied'}` : '',
          organizationResult.status === 'rejected' ? `organisations: ${organizationResult.reason?.message || 'permission denied'}` : ''
        ].filter(Boolean);
        if (failedSections.length) {
          setError(`Some admin data could not be loaded (${failedSections.join('; ')}).`);
        }
      } catch (loadError) {
        if (!isActive) return;
        setError(loadError?.message || 'Failed to load admin access items.');
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      isActive = false;
    };
  }, [user?.uid]);

  const stats = useMemo(() => ({
    sheets: sheets.length,
    workspaces: workspaces.length
  }), [sheets.length, workspaces.length]);

  const openWorkspaceEditor = (workspace) => {
    if (!workspace?.slug) {
      toast.error('Workspace route is missing.');
      return;
    }
    navigate(getWorkspaceEditPath(workspace.slug), {
      state: { returnTo: getWorkspacePath(workspace.slug) }
    });
  };

  const reviewOrganization = async (organization, decision) => {
    setReviewingOrganizationId(organization.id);
    try {
      await organizationService.reviewOrganization(organization.id, decision);
      setPendingOrganizations((current) => current.filter((item) => item.id !== organization.id));
      toast.success(`Organisation ${decision}.`);
    } catch (reviewError) {
      toast.error(reviewError?.message || 'Failed to review organisation.');
    } finally {
      setReviewingOrganizationId('');
    }
  };

  return (
    <div className="space-y-3 w-full max-w-5xl mx-auto">
      <SheetsHeader
        activeTab="admin-access"
        badge="Admin Access"
        backPath="/sheets"
        backAriaLabel="Back to sheets"
        title="Admin Builder Access"
        description="Only sheets and workspaces whose owners explicitly enabled admin access appear here. Open a sheet in read-only view or manage its structure in the builder."
      />

      <section className="grid gap-4 md:grid-cols-2">
        <div className="rounded-[10px] border border-slate-200 bg-white p-5 shadow-md">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Enabled Sheets</p>
          <p className="mt-3 text-3xl font-bold text-slate-900">{stats.sheets}</p>
        </div>
        <div className="rounded-[10px] border border-slate-200 bg-white p-5 shadow-md">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Enabled Workspaces</p>
          <p className="mt-3 text-3xl font-bold text-slate-900">{stats.workspaces}</p>
        </div>
      </section>

      {loading && (
        <div className="rounded-[10px] border border-slate-200 bg-white px-6 py-10 text-center text-slate-500 shadow-md">
          Loading admin access items...
        </div>
      )}

      {!loading && !user?.uid && (
        <div className="rounded-[10px] border border-slate-200 bg-white px-8 py-14 text-center shadow-md">
          <Shield className="mx-auto h-10 w-10 text-[#008080]" />
          <h2 className="mt-5 text-2xl font-semibold text-slate-900">Sign in to use the admin access section</h2>
        </div>
      )}

      {!loading && user?.uid && !isAdmin && (
        <div className="rounded-[10px] border border-amber-200 bg-amber-50 px-8 py-10 text-center text-amber-900 shadow-md">
          <Lock className="mx-auto h-10 w-10" />
          <h2 className="mt-4 text-2xl font-semibold">Admin role required</h2>
          <p className="mt-2 text-sm">
            This route is limited to users whose `parrotdocs-users` role is set to `admin`.
          </p>
        </div>
      )}

      {!loading && isAdmin && error && (
        <div className="rounded-[10px] border border-rose-200 bg-rose-50 px-6 py-5 text-rose-700 shadow-md">
          {error}
        </div>
      )}

      {!loading && isAdmin && (
        <div className="space-y-6">
          <section className="rounded-[10px] border border-slate-200 bg-white p-5 shadow-md">
            <div className="flex items-center gap-3">
              <Shield className="h-5 w-5 text-[#008080]" />
              <h2 className="text-lg font-bold text-slate-900">Pending organisations</h2>
            </div>
            <div className="mt-4 space-y-3">
              {pendingOrganizations.length === 0 ? (
                <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">No organisation requests await approval.</p>
              ) : pendingOrganizations.map((organization) => (
                <article key={organization.id} className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-4">
                  <div>
                    <p className="font-semibold text-slate-900">{organization.name}</p>
                    <p className="mt-1 text-xs text-slate-500">{organization.type}{organization.location ? ` · ${organization.location}` : ''}</p>
                  </div>
                  <div className="flex gap-2">
                    <button type="button" disabled={reviewingOrganizationId === organization.id} onClick={() => reviewOrganization(organization, 'approved')} className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50">Approve</button>
                    <button type="button" disabled={reviewingOrganizationId === organization.id} onClick={() => reviewOrganization(organization, 'rejected')} className="rounded-lg bg-rose-100 px-3 py-2 text-xs font-semibold text-rose-700 disabled:opacity-50">Reject</button>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <div className="grid gap-6 xl:grid-cols-2">
          <section className="rounded-[10px] border border-slate-200 bg-white p-5 shadow-md">
            <div className="flex items-center gap-3">
              <Settings2 className="h-5 w-5 text-[#008080]" />
              <h2 className="text-lg font-bold text-slate-900">Sheets</h2>
            </div>
            <div className="mt-4 space-y-3">
              {sheets.length === 0 ? (
                <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                  No sheets currently expose admin builder access.
                </p>
              ) : sheets.map((sheet) => (
                <article key={sheet.id} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-900">{sheet.title || 'Untitled Sheet'}</p>
                      <p className="mt-1 text-xs text-slate-500">{sheet.description || 'No description provided.'}</p>
                      <div className="mt-3 flex flex-wrap gap-2 text-[11px] font-semibold text-slate-600">
                        <span className="rounded-full bg-white px-3 py-1">{sheet.visibility || 'private'}</span>
                        <span className="rounded-full bg-white px-3 py-1">{sheet.workspaceTitle || 'No workspace'}</span>
                        <span className="rounded-full bg-white px-3 py-1">
                          Admin access via {sheet.adminAccessSource === 'workspace' ? 'workspace' : 'sheet'}
                        </span>
                        <span className="rounded-full bg-white px-3 py-1">Updated {formatDate(sheet.updatedAt || sheet.createdAt)}</span>
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-col gap-2">
                      <button
                        type="button"
                        onClick={() => navigate(getSheetPath(sheet.id), { state: { fromAdminAccess: true } })}
                        className="inline-flex items-center justify-center gap-2 rounded-lg border border-[#008080]/30 bg-white px-3 py-2 text-xs font-semibold text-[#008080] transition hover:bg-[#008080]/5"
                      >
                        <Eye className="h-4 w-4" />
                        View Sheet
                      </button>
                      <button
                        type="button"
                        onClick={() => navigate(`/sheets/${sheet.id}/edit`)}
                        className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#008080] px-3 py-2 text-xs font-semibold text-white transition hover:bg-[#006d6d]"
                      >
                        <Pencil className="h-4 w-4" />
                        Open Builder
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="rounded-[10px] border border-slate-200 bg-white p-5 shadow-md">
            <div className="flex items-center gap-3">
              <FolderCog className="h-5 w-5 text-[#008080]" />
              <h2 className="text-lg font-bold text-slate-900">Workspaces</h2>
            </div>
            <div className="mt-4 space-y-3">
              {workspaces.length === 0 ? (
                <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                  No workspaces currently expose admin builder access.
                </p>
              ) : workspaces.map((workspace) => (
                <article key={workspace.id} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-900">{workspace.title || 'Untitled Workspace'}</p>
                      <p className="mt-1 text-xs text-slate-500">{workspace.description || 'No description provided.'}</p>
                      <div className="mt-3 flex flex-wrap gap-2 text-[11px] font-semibold text-slate-600">
                        <span className="rounded-full bg-white px-3 py-1">{workspace.visibility || 'private'}</span>
                        <span className="rounded-full bg-white px-3 py-1">{workspace.slug || 'No slug'}</span>
                        <span className="rounded-full bg-white px-3 py-1">Updated {formatDate(workspace.updatedAt || workspace.createdAt)}</span>
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-col gap-2">
                      <button
                        type="button"
                        onClick={() => navigate(getWorkspacePath(workspace.slug), { state: { fromAdminAccess: true } })}
                        disabled={!workspace.slug}
                        className="inline-flex items-center justify-center gap-2 rounded-lg border border-[#008080]/30 bg-white px-3 py-2 text-xs font-semibold text-[#008080] transition hover:bg-[#008080]/5 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Eye className="h-4 w-4" />
                        View Workspace
                      </button>
                      <button
                        type="button"
                        onClick={() => openWorkspaceEditor(workspace)}
                        className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#008080] px-3 py-2 text-xs font-semibold text-white transition hover:bg-[#006d6d]"
                      >
                        <Pencil className="h-4 w-4" />
                        Open Editor
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
          </div>
        </div>
      )}
    </div>
  );
};

export default SheetsAdminAccess;
